import React, { useEffect, useMemo, useState } from "react";
import styles from "../topic.module.css";

import { api } from "../../api/api";
import { normalizeListing, normalizeToMapPoints } from "../../services/mapAdapter";
import { useModuleContext } from "../ModuleContext";
import { cachedLoad } from "../cache";
import { SideList, SideListItem, SideListMessage } from "../SideList";

const TEXT = {
  all: { de: "Alle", en: "All" },
  empty: { de: "Noch keine Einträge hinterlegt.", en: "No entries yet." },
  emptyCategory: { de: "Keine Einträge in dieser Kategorie.", en: "No entries in this category." },
};
const t = (entry, language) => entry[language] ?? entry.de;

// Shared panel for every listing type (Gastronomie, Ferienhäuser, …):
// category buttons under the tab, the entries as a list next to the map
// (SideList) and as points on the map. Clicking an entry opens it in the
// detail panel and moves the map there.
export default function ListingPanel({ type }) {
  const { language, showPoints, selectItem, selectedItem, focusMap, setLoading } = useModuleContext();

  const [categories, setCategories] = useState([]);
  // null = "Alle"
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // ----------------------------
  // categories + entries of this type
  // ----------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      cachedLoad(`listing-categories-${type}-${language}`, () => api.listingCategories(type, language)),
      cachedLoad(`listing-${type}-${language}`, () => api.listing(type, language)),
    ])
      .then(([cats, data]) => {
        if (cancelled) return;
        setCategories(cats);
        setItems(normalizeListing(data));
        setLoaded(true);
      })
      .catch((err) => console.error("Listing load failed:", err))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [type, language, setLoading]);

  const categoryName = useMemo(
    () => Object.fromEntries(categories.map((c) => [String(c.id), c.name])),
    [categories],
  );

  const visibleItems = useMemo(
    () =>
      selectedCategory == null
        ? items
        : items.filter((i) => String(i.categoryId) === String(selectedCategory)),
    [items, selectedCategory],
  );

  // map shows exactly what the list shows
  useEffect(() => {
    if (loaded) showPoints(normalizeToMapPoints(visibleItems, type));
  }, [visibleItems, loaded, type, showPoints]);

  // keep an open detail panel in the current language
  useEffect(() => {
    if (!selectedItem?.key) return;
    const fresh = items.find((i) => i.key === selectedItem.key);
    if (fresh && fresh !== selectedItem) selectItem(fresh);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const openItem = (item) => {
    selectItem(item);
    focusMap(item);
  };

  const categoryButton = (id, label, iconUrl) => (
    <button
      key={id ?? "all"}
      className={
        selectedCategory === id
          ? `${styles.categoryBtn} ${styles.categoryBtnActive}`
          : styles.categoryBtn
      }
      onClick={() => setSelectedCategory(id)}
    >
      {iconUrl && <img src={iconUrl} alt="" className={styles.categoryIcon} />}
      {label}
    </button>
  );

  return (
    <div className={styles["poi-page"]}>
      {/* Kategorien */}
      <div className={styles.categories}>
        {categoryButton(null, t(TEXT.all, language))}
        {categories.map((cat) => categoryButton(cat.id, cat.name, cat.icon_url))}
      </div>

      {loaded && (
        <SideList>
          {!visibleItems.length && (
            <SideListMessage>
              {items.length ? t(TEXT.emptyCategory, language) : t(TEXT.empty, language)}
            </SideListMessage>
          )}
          {visibleItems.map((item) => (
            <SideListItem
              key={item.key}
              icon={item.iconUrl}
              title={item.name}
              meta={[categoryName[String(item.categoryId)], item.address].filter(Boolean).join(" · ")}
              active={selectedItem?.key === item.key}
              onClick={() => openItem(item)}
            />
          ))}
        </SideList>
      )}
    </div>
  );
}
